import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Settings, MailWarning, LockKeyhole, BellRing, MessageSquare } from 'lucide-react'
import { Switch } from '../components/ui/switch'
import Modal from '../components/ui/modal'
import { useLanguage } from '../context/LanguageContext'
import { useTenant } from '../context/TenantContext'
import { useClient } from '../context/ClientContext'
import apiService from '../services/api.service'
import logger from '../utils/logger'
import { sanitizeLogMessage } from '../utils/sanitize'

const AccountSettingFormMenu = () => {
  const navigate = useNavigate()
  const { tenanturlname } = useParams()
  const { t } = useLanguage()
  const { tenant } = useTenant()
  const { client, isAuthenticated } = useClient()
  const [isEmailSubscribed, setIsEmailSubscribed] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false)
  const [showUpdateErrorModal, setShowUpdateErrorModal] = useState(false)

  // Fetch email subscription status on mount
  useEffect(() => {
    const getEmailSubscription = async () => {
      if (!client?.clientId || !tenant?.tenantid) {
        setIsLoading(false)
        return
      }

      try {
        const response = await apiService.post('/api/cards/emailgetsubscription', {
          clientid: client.clientId,
          tenantid: tenant.tenantid,
        })

        setIsEmailSubscribed(response === 1)
      } catch (error) {
        console.error('Error fetching email subscription status:', error)
      } finally {
        setIsLoading(false)
      }
    }

    getEmailSubscription()
  }, [client?.clientId, tenant?.tenantid])

  const handleEmailSubscriptionToggle = async (checked) => {
    // If unchecking (unsubscribing), show confirmation modal
    if (!checked) {
      setShowUnsubscribeModal(true)
      return
    }

    // If checking (subscribing), update immediately
    await updateEmailSubscription(checked)
  }

  const updateEmailSubscription = async (isSubscribed) => {
    try {
      await apiService.put('/api/cards/emailsubscription', {
        clientid: client.clientId,
        tenantid: tenant.tenantid,
        isemailsubscribed: isSubscribed,
      })

      setIsEmailSubscribed(isSubscribed)
    } catch (error) {
      logger.error('Error updating email subscription:', sanitizeLogMessage(error))
      setShowUpdateErrorModal(true)
    }
  }

  const handleUnsubscribeConfirm = async () => {
    setShowUnsubscribeModal(false)
    await updateEmailSubscription(false)
  }

  const handleUnsubscribeCancel = () => {
    setShowUnsubscribeModal(false)
  }

  return (
    <div className="inquiry-form-page">
      {/* Account Settings Form - START */}
      <div className="flex flex-col items-center w-full mx-auto mt-8 px-4">
        {/* Header */}
        <div className="flex items-center gap-2 w-full mb-6">
          <Settings className="w-4 h-4" />
          <h1 className="text-xl font-bold">{t('accountSettings.title')}</h1>
        </div>

        {/* Inquiry Options Card */}
        <div className="w-full bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {tenant?.tenantmasterconfig?.show_email_change && (
            <div 
              className="flex items-center justify-between py-4 px-5 cursor-pointer hover:bg-theme-primary/10 border-b border-[hsla(0,0%,85%,1)] last:border-b-0"
              onClick={() => navigate(`/${tenanturlname}/change-email`)}
            >
              <div className="flex items-center gap-3">
                <span className="w-5 h-5 text-theme-primary">
                  <MailWarning />
                </span>
                <span className="text-sm font-medium text-theme-secondary">
                  {t('accountSettings.changeEmail')}
                </span>
              </div>
              <span className="text-blue-600 text-xl">›</span>
            </div>
          )}

          <div
            className="flex items-center justify-between py-4 px-5 cursor-pointer hover:bg-theme-primary/10 border-b border-[hsla(0,0%,85%,1)] last:border-b-0"
            onClick={() => navigate(`/${tenanturlname}/change-password`)}
          >
            <div className="flex items-center gap-3">
              <span className="w-5 h-5 text-theme-primary">
                <LockKeyhole />
              </span>
              <span className="text-sm font-medium text-theme-secondary">
                {t('accountSettings.changePassword')}
              </span>
            </div>
            <span className="text-blue-600 text-xl">›</span>
          </div>

          <div className="flex items-center justify-between py-4 px-5 border-b border-[hsla(0,0%,85%,1)] last:border-b-0">
            <div className="flex items-center gap-3">
              <span className="w-5 h-5 text-theme-primary">
                <BellRing />
              </span>
              <span className="text-sm font-medium text-theme-secondary">
                {t('accountSettings.emailNotifications')}
              </span>
            </div>
            <span className="text-blue-600 text-xl">
              <Switch
                id="subscribe"
                checked={isEmailSubscribed}
                onCheckedChange={handleEmailSubscriptionToggle}
                disabled={isLoading}
              />
            </span>
          </div>

          {isAuthenticated && tenant?.tenantmasterconfig?.contactus?.show !== false && (
          <div
            className="flex items-center justify-between py-4 px-5 cursor-pointer hover:bg-theme-primary/10 border-b border-[hsla(0,0%,85%,1)] last:border-b-0"
            onClick={() => navigate(`/${tenanturlname}/contact-us`)}
          >
            <div className="flex items-center gap-3">
              <span className="w-5 h-5 text-theme-primary">
                <MessageSquare />
              </span>
              <span className="text-sm font-medium text-theme-secondary">
                {t('contactUs.title') || 'Contact Us'}
              </span>
            </div>
            <span className="text-blue-600 text-xl">›</span>
          </div>
          )}
        </div>
      </div>
      {/* Inquiry Form - END */}

      {/* Unsubscribe Confirmation Modal */}
      <Modal
        open={showUnsubscribeModal}
        onClose={handleUnsubscribeCancel}
        type="confirm"
        title={t('accountSettings.unsubscribeTitle') || 'Unsubscribe from Email Notifications'}
        message={
          t('accountSettings.unsubscribeConfirmation') ||
          'Are you sure you want to unsubscribe from email notifications? You will no longer receive important updates and offers.'
        }
        onConfirm={handleUnsubscribeConfirm}
        confirmText={t('accountSettings.unsubscribe') || 'Unsubscribe'}
        cancelText={t('common.cancel') || 'Cancel'}
      />

      <Modal
        open={showUpdateErrorModal}
        onClose={() => setShowUpdateErrorModal(false)}
        type="error"
        title={t('common.error') || 'Error'}
        message={t('accountSettings.updateError')}
        buttonText={t('common.ok') || 'OK'}
      />
    </div>
  )
}

export default AccountSettingFormMenu
